import { useState } from "react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import {
  Avatar,
  Badge,
  Button,
  ListGroup,
  Numeric,
  SectionHeader,
  SectionHeaderAction,
  getInitials,
  tintForId,
} from "@/ui";
import { MatchHistoryRow } from "@/features/matches/MatchHistoryRow";
import { MyTournamentRow } from "@/features/tournaments/MyTournamentRow";
import { EventCard, type EventCardData } from "@/features/events/EventCard";
import { EventDetailSheet } from "@/features/events/EventDetailSheet";
import {
  useMyMatches,
  useMyTournaments,
  useRatingHistory,
  useTournaments,
  useTrainings,
} from "@/features/queries";
import { useAuth } from "@/contexts/AuthContext";

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Profile shows last 3 of each list with a "Показать все" link to a
  // dedicated paginated page. We over-fetch by 1 to know whether the list
  // would have more than 3 — used to decide whether to render the link.
  const matches = useMyMatches(4);
  const myTournamentsHistory = useMyTournaments(4);
  const rating = useRatingHistory();
  const tournaments = useTournaments();
  const trainings = useTrainings();

  const [selectedEvent, setSelectedEvent] = useState<EventCardData | null>(null);

  if (!user) return null;

  // "Куда я записался" — only show events with my is_registered flag true,
  // excluding already-completed/cancelled ones. Tournaments already filter
  // is_registered server-side; trainings now too.
  const mySignUps: EventCardData[] = [
    ...(tournaments.data ?? [])
      .filter(
        (t) =>
          t.is_registered &&
          t.status !== "completed" &&
          t.status !== "cancelled",
      )
      .map<EventCardData>((data) => ({ kind: "tournament", data })),
    ...(trainings.data ?? [])
      .filter((t) => t.is_registered && t.status === "active")
      .map<EventCardData>((data) => ({ kind: "training", data })),
  ].sort(
    (a, b) =>
      new Date(a.data.date_time).getTime() - new Date(b.data.date_time).getTime(),
  );

  const fullName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
    user.username ||
    "Игрок";

  const points = (rating.data ?? []).map((p) => ({
    ...p,
    label: dayjs(p.date).format("DD.MM"),
  }));

  return (
    <div style={{ paddingBottom: 24 }}>
      <section
        style={{
          padding: "24px 16px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        <Avatar
          initials={getInitials(user.first_name, user.last_name, user.username)}
          size={96}
          tint={tintForId(user.id)}
          src={user.photo_url}
        />
        <div style={{ textAlign: "center" }}>
          <h1 className="h1" style={{ margin: 0 }}>{fullName}</h1>
          {user.username && (
            <div className="caption" style={{ color: "var(--fg2)", marginTop: 4 }}>
              @{user.username}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {user.is_admin && <Badge kind="brand">Админ</Badge>}
          {user.is_trainer && <Badge kind="success">Тренер</Badge>}
        </div>
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <div className="caption" style={{ color: "var(--fg3)" }}>Рейтинг</div>
          <Numeric size={56} color="var(--brand-orange)">{user.rating}</Numeric>
        </div>
      </section>

      {points.length > 1 && (
        <>
          <SectionHeader>Динамика рейтинга</SectionHeader>
          <div
            style={{
              background: "var(--bg-section)",
              borderRadius: "var(--r-lg)",
              margin: "0 16px",
              padding: "16px 8px",
              height: 220,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points}>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "var(--fg3)" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "var(--fg3)" }}
                  axisLine={false}
                  tickLine={false}
                  domain={["dataMin - 20", "dataMax + 20"]}
                  width={32}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--bg)",
                    border: "1px solid var(--separator)",
                    borderRadius: 8,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="rating"
                  stroke="var(--brand-orange)"
                  strokeWidth={2.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {mySignUps.length > 0 && (
        <>
          <SectionHeader>Куда я записался</SectionHeader>
          {mySignUps.map((e) => (
            <EventCard
              key={`${e.kind}-${e.data.id}`}
              event={e}
              onClick={() => setSelectedEvent(e)}
            />
          ))}
        </>
      )}

      <SectionHeaderAction
        title="Последние матчи"
        onAction={
          (matches.data?.length ?? 0) > 3
            ? () => navigate("/profile/matches")
            : undefined
        }
      />
      {matches.data && matches.data.length > 0 ? (
        <ListGroup>
          {matches.data.slice(0, 3).map((m) => <MatchHistoryRow key={m.id} match={m} />)}
        </ListGroup>
      ) : (
        <EmptyBlock text="Здесь появятся твои матчи." />
      )}

      <SectionHeaderAction
        title="Последние турниры"
        onAction={
          (myTournamentsHistory.data?.length ?? 0) > 3
            ? () => navigate("/profile/tournaments")
            : undefined
        }
      />
      {myTournamentsHistory.data && myTournamentsHistory.data.length > 0 ? (
        <ListGroup>
          {myTournamentsHistory.data.slice(0, 3).map((t) => (
            <MyTournamentRow key={t.id} tournament={t} />
          ))}
        </ListGroup>
      ) : (
        <EmptyBlock text="Здесь появятся твои турниры." />
      )}

      <div style={{ padding: "24px 16px 0", display: "flex", flexDirection: "column", gap: 8 }}>
        {user.is_admin ? (
          <Button fullWidth variant="secondary" onClick={() => navigate("/admin")}>
            Открыть админку
          </Button>
        ) : user.is_trainer ? (
          <Button fullWidth variant="secondary" onClick={() => navigate("/admin/trainings")}>
            Кабинет тренера
          </Button>
        ) : null}
        <Button fullWidth variant="ghost" onClick={logout}>
          Выйти
        </Button>
      </div>

      <EventDetailSheet
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </div>
  );
}

function EmptyBlock({ text }: { text: string }) {
  return (
    <div
      style={{
        background: "var(--bg-section)",
        borderRadius: "var(--r-lg)",
        margin: "0 16px",
        padding: 20,
        textAlign: "center",
        color: "var(--fg2)",
      }}
    >
      {text}
    </div>
  );
}
